/**
 * Typed Stage 1 client: query hooks + mutations over the worker contract.
 * Query keys: ['stages'], ['roles'], ['leads', filters], ['lead', id].
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient, type ApiClient } from '@/lib/api-client';
import {
  normalizeLeadDetail,
  type LeadCard,
  type LeadDetail,
  type LeadFilters,
  type LeadNote,
  type LeadPatch,
  type Role,
  type Stage,
  type StageCreate,
  type StageDeleteResult,
  type StagePatch,
  type WorkerLeadDetail,
} from './types';

export * from './types';

export const STAGES_KEY = ['stages'] as const;
export const ROLES_KEY = ['roles'] as const;
export const LEADS_KEY = ['leads'] as const;
export const leadKey = (id: string) => ['lead', id] as const;

export const LEADS_PAGE_LIMIT = 500;

function cleanFilters(f: LeadFilters): LeadFilters {
  const out: LeadFilters = {};
  if (f.q?.trim()) out.q = f.q.trim();
  if (f.role_id) out.role_id = f.role_id;
  if (f.shop_id) out.shop_id = f.shop_id;
  if (f.stage_id) out.stage_id = f.stage_id;
  return out;
}

export function leadsPath(filters: LeadFilters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(cleanFilters(filters))) params.set(k, v);
  params.set('limit', String(LEADS_PAGE_LIMIT));
  return `/api/leads?${params.toString()}`;
}

/** The list endpoint may answer a bare array or `{ items, next_cursor }`. */
function unwrapList<T>(res: T[] | { items: T[] }): T[] {
  return Array.isArray(res) ? res : res.items;
}

export function useStages() {
  const client = useApiClient();
  return useQuery({
    queryKey: STAGES_KEY,
    queryFn: async () =>
      unwrapList(await client.get<Stage[] | { items: Stage[] }>('/api/stages')).sort(
        (a, b) => a.sort_order - b.sort_order,
      ),
    staleTime: 60 * 1000,
  });
}

export function useRoles() {
  const client = useApiClient();
  return useQuery({
    queryKey: ROLES_KEY,
    queryFn: async () => unwrapList(await client.get<Role[] | { items: Role[] }>('/api/roles')),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeads(filters: LeadFilters) {
  const client = useApiClient();
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...LEADS_KEY, clean],
    queryFn: async () =>
      unwrapList(await client.get<LeadCard[] | { items: LeadCard[] }>(leadsPath(clean))),
  });
}

export function useLead(id: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: leadKey(id ?? ''),
    queryFn: async () =>
      normalizeLeadDetail(await client.get<WorkerLeadDetail | LeadDetail>(`/api/leads/${id}`)),
    enabled: !!id,
    // Stage 2: the resume parser runs after the upload response; poll until it settles.
    refetchInterval: (query) => (query.state.data?.resume?.parse_status === 'pending' ? 3000 : false),
  });
}

/** `PATCH /api/leads/:id` answers with the full (nested) detail; flatten it like `useLead`. */
export function patchLead(client: ApiClient, id: string, patch: LeadPatch): Promise<LeadDetail> {
  return client
    .patch<WorkerLeadDetail | LeadDetail>(`/api/leads/${id}`, patch)
    .then(normalizeLeadDetail);
}

export function useAddNote(leadId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => client.post<LeadNote>(`/api/leads/${leadId}/notes`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadKey(leadId) });
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useStageMutations() {
  const client = useApiClient();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: STAGES_KEY });
    void qc.invalidateQueries({ queryKey: LEADS_KEY });
  };
  const create = useMutation({
    mutationFn: (body: StageCreate) => client.post<Stage>('/api/stages', body),
    onSettled: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, ...patch }: StagePatch & { id: string }) =>
      client.patch<Stage>(`/api/stages/${id}`, patch),
    onSettled: refresh,
  });
  const reorder = useMutation({
    mutationFn: (ordered_ids: string[]) => client.post<Stage[]>('/api/stages/reorder', { ordered_ids }),
    onMutate: async (ordered_ids) => {
      await qc.cancelQueries({ queryKey: STAGES_KEY });
      const previous = qc.getQueryData<Stage[]>(STAGES_KEY);
      if (previous) {
        const byId = new Map(previous.map((s) => [s.id, s]));
        qc.setQueryData<Stage[]>(
          STAGES_KEY,
          ordered_ids
            .map((id, i) => byId.get(id) && { ...byId.get(id)!, sort_order: i + 1 })
            .filter((s): s is Stage => !!s),
        );
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(STAGES_KEY, ctx.previous);
    },
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: ({ id, move_to }: { id: string; move_to?: string }) =>
      client.delete<StageDeleteResult | null>(
        `/api/stages/${id}${move_to ? `?move_to=${encodeURIComponent(move_to)}` : ''}`,
      ),
    onSettled: refresh,
  });
  return { create, update, reorder, remove };
}
